/**
 * Folha (bottom sheet) — o contêiner de toda seleção do app.
 *
 * Existe por dois motivos concretos, os dois encontrados na prática:
 *
 * 1. **Portal, para não ser recortada.** A versão anterior era um `absolute`
 *    dentro da barra de filtros, que tem `overflow-x: auto` para rolar os chips
 *    no celular. Elemento absoluto dentro de contêiner com overflow é
 *    recortado pelo navegador — o menu simplesmente não aparecia, e a altura
 *    dele ainda criava uma barra de rolagem no meio da barra. Renderizar em
 *    `document.body` elimina a classe inteira desse problema: nenhum ancestral
 *    pode recortar o que não é filho dele.
 *
 * 2. **Sobrepor, não empurrar.** Painel que abre no fluxo desloca os gráficos
 *    para baixo e faz o usuário perder o lugar onde estava. A folha flutua por
 *    cima e devolve a tela intacta ao fechar.
 *
 * No celular sobe de baixo, onde o polegar alcança. A partir de 640px vira uma
 * caixa centrada, que é o gesto esperado no desktop.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Altura útil da tela, descontando o teclado virtual.
 *
 * `100dvh` não resolve: no Android e no iOS o teclado não encolhe o viewport de
 * layout, então a folha continua ancorada no fundo da página — atrás do
 * teclado. O resultado é o que se via ao buscar um consultor: a lista aparecia
 * embaixo do teclado e só dava para escolher fechando ele antes.
 *
 * `visualViewport` é a única medida que enxerga o teclado. Onde ela não existe,
 * caímos em `innerHeight`, que é o comportamento antigo.
 */
function useAlturaVisivel(): number | null {
  const [altura, setAltura] = useState<number | null>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const medir = () => setAltura(vv.height);
    medir();

    vv.addEventListener('resize', medir);
    // O iOS "rola" o viewport visual ao abrir o teclado em vez de redimensionar.
    vv.addEventListener('scroll', medir);
    return () => {
      vv.removeEventListener('resize', medir);
      vv.removeEventListener('scroll', medir);
    };
  }, []);

  return altura;
}

export default function Folha({
  aberta,
  titulo,
  onFechar,
  children,
  larga,
}: {
  aberta: boolean;
  titulo: string;
  onFechar: () => void;
  children: ReactNode;
  /** Caixa mais larga no desktop — para conteúdo que não é lista, como uma prévia. */
  larga?: boolean;
}) {
  const painel = useRef<HTMLDivElement>(null);
  const alturaVisivel = useAlturaVisivel();

  useEffect(() => {
    if (!aberta) return;

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    document.addEventListener('keydown', aoTeclar);

    // Trava a rolagem do fundo: sem isto, rolar dentro da folha "vaza" para a
    // página atrás, que é o incômodo clássico de modal no celular.
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Devolve o foco a quem abriu, ao fechar — senão o teclado volta para o
    // topo da página.
    const focoAnterior = document.activeElement as HTMLElement | null;
    painel.current?.focus();

    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = overflowAnterior;
      focoAnterior?.focus?.();
    };
  }, [aberta, onFechar]);

  if (!aberta) return null;

  return createPortal(
    <div
      className="fixed inset-x-0 top-0 z-50 flex items-end justify-center sm:items-center"
      // Amarra o contêiner à altura visível: com o teclado aberto, "fundo da
      // tela" passa a ser o topo do teclado, e a folha sobe junto.
      style={{ height: alturaVisivel ?? '100dvh' }}
    >
      {/* Fundo escurecido: fechar tocando fora é o gesto esperado. */}
      <button
        aria-label="Fechar"
        onClick={onFechar}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-[2px]"
      />

      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
        className={[
          'relative flex w-full flex-col outline-none',
          'border border-[var(--border)] bg-[var(--surface)]',
          'rounded-t-[16px] sm:rounded-[12px]',
          larga ? 'sm:max-w-3xl' : 'sm:max-w-md',
          'shadow-[0_-8px_40px_rgb(0_0_0/0.28)] sm:shadow-[0_16px_48px_rgb(0_0_0/0.28)]',
          'animate-[subir_180ms_ease-out]',
        ].join(' ')}
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          // 85% do que está visível — não da tela inteira.
          maxHeight: alturaVisivel ? alturaVisivel * 0.92 : '85dvh',
        }}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--border)] px-4 py-3">
          <h2 className="text-sm font-medium text-[var(--fg)]">{titulo}</h2>
          <button
            onClick={onFechar}
            aria-label="Fechar"
            className="anel-foco grid size-8 place-items-center rounded-[var(--r-md)] text-[var(--muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--fg)]"
          >
            <X size={16} />
          </button>
        </div>

        {/* A rolagem vive aqui dentro, não na página. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
